import { useMaestroStore } from '../store/maestroStore';
import type { PlaybackState } from '../protocols/BATON';
import { TransportCommand } from './TransportCommand';
import { detectPlatform } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  getStartupDisplayVolume,
  normalizeVolumePercent,
  resolveFetchedVolume,
  resolveInboundDeviceVolume,
  writeVolumeCache,
} from './volumeHydration';
import {
  reconcilePlaybackStateFromRenderer,
} from './nowPlayingReconcile';
import {
  clearReconcileDegraded,
  isReconcileDegraded,
  markReconcileDegraded,
  scheduleReconcileRecoveryProbe,
} from './backgroundTransportReconcile';
import {
  enableWallClockSynthesisForStall,
  resetStaleRelTimeTracking,
} from './remoteAdvanceControl';
import {
  buildAdvanceSuppressedWallClockLog,
  capSynthesisPosition,
  evaluateWallClockAdvanceGuard,
  getEffectiveStartOffsetSecs,
  getSincePlayUriMs,
  isPositionAtGenuineTrackEnd,
  recordFrozenEndWallClockSuppression,
  setSuppressWallClockAdvance,
} from './positionReconcile';

function parseCurrentTransportState(rawResp: string): string {
  const match = rawResp.match(/<CurrentTransportState>(.*?)<\/CurrentTransportState>/);
  return match?.[1] || 'UNKNOWN';
}

function isNetworkRendererZone(zone: any): boolean {
  if (!zone?.type) return false;
  const localTypes = ['browser', 'local', 'local-native'];
  const localIds = ['this_computer', 'local_computer', 'this_device'];
  return !localTypes.includes(zone.type) && !localIds.includes(zone.id);
}

type ConnectorModule = typeof import('../lib/connector');

export type TransportOwner = 'queue' | 'radio' | 'direct' | null;

class TransportStateImpl {
  private _initialized = false;
  /** Exactly one surface owns transport commands at a time. */
  private _transportOwner: TransportOwner = null;
  private _unlistenTransport: (() => void) | null = null;
  private _unlistenTrackEnded: (() => void) | null = null;
  private _unlistenExclusiveFailed: (() => void) | null = null;
  private _unlistenExclusiveStarted: (() => void) | null = null;
  private _unlistenVolume: (() => void) | null = null;
  private _unlistenPosition: (() => void) | null = null;
  private _unsubscribeStore: (() => void) | null = null;
  private _unsubscribePlaybackPower: (() => void) | null = null;
  private _lastFetchedRendererId: string | null = null;
  private _fetchAttempts: Map<string, number> = new Map();
  private _retryTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly FETCH_MAX_ATTEMPTS = 4;
  private static readonly FETCH_RETRY_DELAYS_MS = [150, 750, 2000, 5000];

  private _connector: ConnectorModule | null = null;
  private _iosCheckLogged = false;
  private _wakeHeartbeat = Date.now();
  private _wakeTimer: ReturnType<typeof setInterval> | null = null;
  private _playbackPowerTimer: ReturnType<typeof setInterval> | null = null;
  private _stallMonitorTimer: ReturnType<typeof setInterval> | null = null;
  private _nowPlayingReconcileTimer: ReturnType<typeof setInterval> | null = null;
  private _nowPlayingReconcileInFlight = false;
  private _nowPlayingReconcileTick: (() => void) | null = null;
  private _lastObservedPosition = 0;
  private _lastPositionChangeAt = 0;
  private _stallReconcileInFlight = false;
  private _stallReconcileCooldownUntil = 0;
  private static readonly PLAYBACK_POWER_HEARTBEAT_MS = 2000;
  private static readonly POSITION_STALL_RECONCILE_MS = 12_000;
  private static readonly STALL_RECONCILE_COOLDOWN_MS = 60_000;
  private static readonly NOW_PLAYING_RECONCILE_MS = 10_000;

  private async _getConnector(): Promise<ConnectorModule> {
    if (!this._connector) {
      this._connector = await import('../lib/connector');
    }
    return this._connector;
  }

  init() {
    if (this._initialized) return;
    this._initialized = true;
    this._transportOwner = null;

    if (this._isTauri()) {
      this._listenTauri();
      this._watchPlaybackPowerManagement();
      this._startWakeRecovery();
      this._startPositionStallMonitor();
      this._startNowPlayingReconcileMonitor();
    }

    if (this._isIOS()) {
      this._listenIOS();
    }

    this._watchActiveRenderer();
  }

  // Watch for activeConnectorRenderer id transitions (zone switch OR initial restore
  // from localStorage) and immediately fetch the renderer's current volume.
  // The Rust volume_monitor only emits on change, so it won't re-push an unchanged
  // cached value — we have to ask explicitly.
  private _watchActiveRenderer() {
    const fetchIfNeeded = () => {
      const state = useMaestroStore.getState();
      const id = state.activeConnectorRenderer?.id ?? null;
      if (!id) return;

      // Check fixed-volume using BOTH activeZone AND discoveredZones — the
      // activeZone may be a partial shim from the sync handler before the
      // full zone reconciliation lands.
      const activeZone = state.activeZone as any;
      const zoneIsFixed = !!activeZone?.capabilities?.fixedVolume;
      const fromList = state.discovery?.discoveredZones?.find((z: any) => z.id === id);
      const listIsFixed = !!fromList?.capabilities?.fixedVolume;
      if (zoneIsFixed || listIsFixed) {
        // Mark as handled so we don't keep retrying — fixed-volume devices
        // genuinely have no queryable volume.
        this._lastFetchedRendererId = id;
        this._fetchAttempts.delete(id);
        return;
      }

      if (id === this._lastFetchedRendererId) return;
      this._lastFetchedRendererId = id;
      this._fetchAttempts.set(id, 0);

      // Optimistic hydrate — last known per zone, or 35%; never 0 before device confirms.
      if (state.volume == null) {
        state.setVolume(getStartupDisplayVolume(id));
      }

      this._scheduleFetch(id);
    };

    fetchIfNeeded();
    this._unsubscribeStore = useMaestroStore.subscribe(() => {
      fetchIfNeeded();
      this._persistActiveVolume();
    });
  }

  private _scheduleFetch(rendererId: string): void {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    const attempt = this._fetchAttempts.get(rendererId) ?? 0;
    if (attempt >= TransportStateImpl.FETCH_MAX_ATTEMPTS) return;
    const delay = TransportStateImpl.FETCH_RETRY_DELAYS_MS[attempt] ?? 5000;
    this._retryTimer = setTimeout(() => {
      this._retryTimer = null;
      // Abort if the active renderer has changed in the meantime.
      const currentId = useMaestroStore.getState().activeConnectorRenderer?.id ?? null;
      if (currentId !== rendererId) return;
      this._fetchRendererVolume(rendererId);
    }, delay);
  }

  private _fetchRendererVolume(rendererId: string): void {
    const p = detectPlatform();
    const isTauri = p.isTauriDesktop;
    const native = p.isIOSNativeApp ? (window as any).__maestroNative : null;
    const attempt = (this._fetchAttempts.get(rendererId) ?? 0) + 1;
    this._fetchAttempts.set(rendererId, attempt);

    if (isTauri) {
      this._getConnector()
        .then(({ getVolumeBackground }) =>
          getVolumeBackground(rendererId).then((vol) => {
            const resolved = resolveFetchedVolume(rendererId, vol);
            console.log('[TransportState] Volume fetch OK:', rendererId, vol, '→', resolved, `(attempt ${attempt})`);
            useMaestroStore.getState().setVolume(resolved);
            writeVolumeCache(rendererId, resolved);
            this._fetchAttempts.delete(rendererId);
            if (isReconcileDegraded()) {
              clearReconcileDegraded('volume-background');
              this._restartNowPlayingReconcileMonitor();
            }
          })
        )
        .catch((e) => {
          void this._getConnector().then(({ isLocalTiersExhaustedError }) => {
            if (isLocalTiersExhaustedError(e)) {
              console.debug('[TransportState] volume hydration halted — local tiers exhausted');
              this._enterReconcileDegraded('volume-hydration');
              return;
            }
            console.debug(`[TransportState] get_volume failed attempt ${attempt}/${TransportStateImpl.FETCH_MAX_ATTEMPTS}:`, e);
            if (attempt < TransportStateImpl.FETCH_MAX_ATTEMPTS) {
              this._scheduleFetch(rendererId);
            }
          });
        });
    } else if (native?.getVolume) {
      try {
        native.getVolume(rendererId);
      } catch {}
    }
  }

  private _persistActiveVolume(): void {
    const state = useMaestroStore.getState();
    const id = state.activeConnectorRenderer?.id;
    if (id && typeof state.volume === 'number' && state.volume > 0) {
      writeVolumeCache(id, state.volume);
    }
  }

  handleBatonEvent(event: string, payload: any) {
    const store = useMaestroStore.getState();
    switch (event) {
      case 'playbackStateChanged':
        if (
          payload.state === 'paused'
          && TransportCommand.isInPlayUriPostSuccessSilenceWindow()
        ) {
          console.log('[Sync] Ignoring stale pause — within 2s of playUri');
          break;
        }
        if (
          payload.state === 'paused'
          && store.rendererHasStream
          && (store.playbackState === 'playing' || store.playbackState === 'loading')
        ) {
          console.log('[TransportState] Ignoring BATON playbackStateChanged pause — renderer stream');
          break;
        }
        store.setPlaybackState(payload.state);
        break;
      case 'positionChanged':
        store.updateTime(payload.position, payload.duration);
        break;
      case 'trackChanged':
        store.setCurrentTrack(payload.track);
        if (payload.duration) store.setDuration(payload.duration);
        break;
      case 'volumeChanged': {
        const id = store.activeConnectorRenderer?.id;
        if (TransportCommand.isVolumeEchoSuppressed(payload.level)) break;
        const resolved = id
          ? resolveFetchedVolume(id, payload.level)
          : normalizeVolumePercent(payload.level);
        store.setVolume(resolved);
        break;
      }
    }
  }

  syncFromServer(serverState: {
    playbackState: string;
    currentTrack: any;
    position: number;
    duration: number;
    volume: number;
    queueIndex: number;
  }) {
    const store = useMaestroStore.getState();
    store.setPlaybackState(serverState.playbackState as any);
    store.setCurrentTrack(serverState.currentTrack);
    store.updateTime(serverState.position, serverState.duration);
    store.setVolume(serverState.volume);
    store.setQueueIndex(serverState.queueIndex);
  }

  destroy() {
    this._unlistenTransport?.();
    this._unlistenVolume?.();
    this._unlistenPosition?.();
    this._unsubscribePlaybackPower?.();
    this._unsubscribePlaybackPower = null;
    if (this._playbackPowerTimer) {
      clearInterval(this._playbackPowerTimer);
      this._playbackPowerTimer = null;
    }
    if (this._wakeTimer) {
      clearInterval(this._wakeTimer);
      this._wakeTimer = null;
    }
    if (this._stallMonitorTimer) {
      clearInterval(this._stallMonitorTimer);
      this._stallMonitorTimer = null;
    }
    if (this._nowPlayingReconcileTimer) {
      clearInterval(this._nowPlayingReconcileTimer);
      this._nowPlayingReconcileTimer = null;
    }
    this._nowPlayingReconcileTick = null;
    this._unsubscribeStore?.();
    this._unsubscribeStore = null;
    this._lastFetchedRendererId = null;
    this._connector = null;
    this._initialized = false;
  }

  /** Connector desktop: heartbeats playback-active to defeat App Nap / idle sleep while playing. */
  private _watchPlaybackPowerManagement() {
    if (!this._isTauri()) return;

    const pulse = (active: boolean) => {
      void this._getConnector().then(({ setPlaybackActive }) => {
        void setPlaybackActive(active);
      });
    };

    const sync = () => {
      const state = useMaestroStore.getState().playbackState;
      const holdPower = state === 'playing' || state === 'buffering';
      if (this._playbackPowerTimer) {
        clearInterval(this._playbackPowerTimer);
        this._playbackPowerTimer = null;
      }
      if (!holdPower) {
        void import('../radio/ellaRadioSessionState').then(({ isRadioWaiting }) => {
          if (!isRadioWaiting()) pulse(false);
        });
        return;
      }
      pulse(true);
      this._playbackPowerTimer = setInterval(() => {
        const s = useMaestroStore.getState().playbackState;
        const stillHold = s === 'playing' || s === 'buffering';
        if (stillHold) {
          pulse(true);
          return;
        }
        void import('../radio/ellaRadioSessionState').then(({ isRadioWaiting }) => {
          if (isRadioWaiting()) pulse(true);
          else pulse(false);
        });
      }, TransportStateImpl.PLAYBACK_POWER_HEARTBEAT_MS);
    };

    sync();
    this._unsubscribePlaybackPower = useMaestroStore.subscribe(() => {
      sync();
    });
  }

  /** Detect system wake by monitoring heartbeat gaps.
   *  If the timer fires after a 15s+ gap (impossible unless the system slept),
   *  attempt recovery: re-assert sleep prevention and verify renderer state. */
  private _startWakeRecovery() {
    const HEARTBEAT_MS = 3000;
    const SLEEP_GAP_MS = 15000;

    this._wakeHeartbeat = Date.now();
    this._wakeTimer = setInterval(() => {
      const now = Date.now();
      const gap = now - this._wakeHeartbeat;
      this._wakeHeartbeat = now;

      if (gap > SLEEP_GAP_MS) {
        console.log(`[WakeRecovery] System wake detected — gap was ${Math.round(gap / 1000)}s`);
        this._handleSystemWake(gap);
      }
    }, HEARTBEAT_MS);
  }

  private async _handleSystemWake(_gapMs: number) {
    const state = useMaestroStore.getState();
    if (state.playbackState !== 'playing') return;

    console.log('[WakeRecovery] Was playing before sleep — recovering');

    // Re-assert sleep prevention (may have been cleared during sleep)
    try {
      const C = await this._getConnector();
      void C.setPlaybackActive(true);
    } catch {}

    // Give network 3s to reconnect after wake
    await new Promise(r => setTimeout(r, 3000));

    // Verify renderer state via get_transport_info
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    if (!renderer?.id) return;

    try {
      const C = await this._getConnector();
      const rawResp: string = await C.getTransportInfoBackground(renderer.id);
      const rendererState = parseCurrentTransportState(rawResp);
      console.log('[WakeRecovery] Renderer transport state:', rendererState);

      if (rendererState === 'STOPPED' || rendererState === 'NO_MEDIA_PRESENT') {
        console.log('[WakeRecovery] Track ended during sleep — advancing');
        void TransportCommand.next();
      } else if (rendererState === 'PLAYING') {
        console.log('[WakeRecovery] Renderer still playing — OK');
      }
    } catch (err) {
      const C = await this._getConnector();
      if (C.isLocalTiersExhaustedError(err)) {
        console.warn('[WakeRecovery] local tiers exhausted — halting background reconcile');
        this.enterBackgroundReconcileDegraded('wake-recovery');
        return;
      }
      console.error('[WakeRecovery] Cannot reach renderer — will recover on next user action:', err);
    }
  }

  /** Detect frozen store.position during network playback and reconcile via GetTransportInfo. */
  private _startPositionStallMonitor() {
    this._lastPositionChangeAt = Date.now();
    this._lastObservedPosition = useMaestroStore.getState().position || 0;

    this._stallMonitorTimer = setInterval(() => {
      const store = useMaestroStore.getState();
      if (TransportCommand.isExclusiveActive()) return;

      const zone = store.discovery?.activeZone;
      if (!isNetworkRendererZone(zone)) {
        this._lastObservedPosition = store.position || 0;
        this._lastPositionChangeAt = Date.now();
        return;
      }

      if (store.playbackState !== 'playing') {
        this._lastObservedPosition = store.position || 0;
        this._lastPositionChangeAt = Date.now();
        return;
      }

      const pos = store.position || 0;
      if (pos !== this._lastObservedPosition) {
        this._lastObservedPosition = pos;
        this._lastPositionChangeAt = Date.now();
        return;
      }

      const frozenMs = Date.now() - this._lastPositionChangeAt;
      if (
        frozenMs >= TransportStateImpl.POSITION_STALL_RECONCILE_MS
        && Date.now() >= this._stallReconcileCooldownUntil
        && !isReconcileDegraded()
      ) {
        void this._reconcileOnPositionStall(frozenMs);
      }
    }, 1000);
  }

  /** Read-only reconcile: resume position clock or sync displayed state — no play/pause. */
  private async _reconcileOnPositionStall(frozenMs: number) {
    if (this._stallReconcileInFlight) return;
    this._stallReconcileInFlight = true;
    this._stallReconcileCooldownUntil = Date.now() + TransportStateImpl.STALL_RECONCILE_COOLDOWN_MS;

    try {
      const store = useMaestroStore.getState();
      if (TransportCommand.isExclusiveActive()) return;
      if (store.playbackState !== 'playing') return;

      const renderer = store.activeConnectorRenderer;
      if (!renderer?.id) return;

      const C = await this._getConnector();
      const rawResp: string = await C.getTransportInfoBackground(renderer.id);
      const rendererState = parseCurrentTransportState(rawResp);

      diagnosticLog('info', 'playback', '[PositionReconcile] stall reconcile via GetTransportInfo', {
        rendererId: renderer.id,
        rendererState,
        frozenSecs: Math.round(frozenMs / 1000),
        position: store.position,
        duration: store.duration,
      });

      if (rendererState === 'PLAYING' || rendererState === 'TRANSITIONING') {
        const { position, duration } = store;
        const atTrackEnd = isPositionAtGenuineTrackEnd(position, duration);
        if (atTrackEnd) {
          const sincePlayUriMs = getSincePlayUriMs();
          const effectiveStartOffsetSecs = getEffectiveStartOffsetSecs();
          const guard = evaluateWallClockAdvanceGuard({
            durationSecs: duration,
            sincePlayUriMs,
            effectiveStartOffsetSecs,
            positionSecs: position,
            rendererState,
          });
          if (!guard.allow) {
            if (guard.atGenuineEnd) {
              recordFrozenEndWallClockSuppression();
            }
            const capped = capSynthesisPosition(duration);
            store.updateTime(capped, duration);
            enableWallClockSynthesisForStall();
            diagnosticLog(
              'info',
              'playback',
              '[playback] advance_suppressed_wall_clock',
              buildAdvanceSuppressedWallClockLog(
                renderer.id,
                position,
                duration,
                rendererState,
                sincePlayUriMs,
                effectiveStartOffsetSecs,
                { path: 'frozen_duration' },
              ),
            );
            return;
          }
          const { QueueController } = await import('./QueueController');
          console.log('[PositionReconcile] Frozen at track end while PLAYING — advancing queue');
          diagnosticLog('info', 'playback', '[PositionReconcile] end-of-track advance (frozen at duration)', {
            rendererId: renderer.id,
            frozenSecs: Math.round(frozenMs / 1000),
            position,
            duration,
            sincePlayUri: sincePlayUriMs,
            effective_start_offset: effectiveStartOffsetSecs,
            forcedAtFrozenEnd: guard.forcedAtFrozenEnd,
          });
          void QueueController.handleRendererTrackEnded(renderer.id, 'stall-at-end');
          return;
        }
        enableWallClockSynthesisForStall();
        console.log('[PositionReconcile] Renderer still PLAYING — resuming wall-clock position synthesis');
        return;
      }

      if (rendererState === 'PAUSED_PLAYBACK') {
        store.setPlaybackState('paused');
        resetStaleRelTimeTracking();
        return;
      }

      if (rendererState === 'STOPPED' || rendererState === 'NO_MEDIA_PRESENT') {
        const { position, duration } = store;
        const atGenuineEnd = isPositionAtGenuineTrackEnd(position, duration);
        store.setPlaybackState('stopped');
        resetStaleRelTimeTracking();

        if (atGenuineEnd) {
          const { QueueController } = await import('./QueueController');
          void QueueController.handleRendererTrackEnded(renderer.id, 'stall-reconcile');
        }
      }
    } catch (err) {
      const C = await this._getConnector();
      if (C.isLocalTiersExhaustedError(err)) {
        console.warn('[PositionReconcile] local tiers exhausted — halting background reconcile');
        this._enterReconcileDegraded('position-stall');
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      console.warn('[PositionReconcile] GetTransportInfo failed:', message);
    } finally {
      this._stallReconcileInFlight = false;
    }
  }

  /** Periodic authoritative play-state sync when UI may have drifted from renderer. */
  private _startNowPlayingReconcileMonitor() {
    const tick = () => {
      if (isReconcileDegraded()) return;
      if (document.visibilityState === 'hidden') return;
      void this._reconcileNowPlayingFromRenderer('interval');
    };

    this._nowPlayingReconcileTick = tick;
    this._nowPlayingReconcileTimer = setInterval(tick, TransportStateImpl.NOW_PLAYING_RECONCILE_MS);

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && !isReconcileDegraded()) {
          void this._reconcileNowPlayingFromRenderer('focus');
        }
      });
    }
  }

  private _stopNowPlayingReconcileMonitor(): void {
    if (this._nowPlayingReconcileTimer) {
      clearInterval(this._nowPlayingReconcileTimer);
      this._nowPlayingReconcileTimer = null;
    }
  }

  private _restartNowPlayingReconcileMonitor(): void {
    if (this._nowPlayingReconcileTimer || !this._nowPlayingReconcileTick) return;
    this._nowPlayingReconcileTimer = setInterval(
      this._nowPlayingReconcileTick,
      TransportStateImpl.NOW_PLAYING_RECONCILE_MS,
    );
  }

  /** Halt background transport polls when local tiers exhaust (#445 / #456). */
  enterBackgroundReconcileDegraded(source: string): void {
    this._enterReconcileDegraded(source);
  }

  private _enterReconcileDegraded(source: string): void {
    markReconcileDegraded(source);
    this._stopNowPlayingReconcileMonitor();
    scheduleReconcileRecoveryProbe(() => {
      void this._probeLocalReconcileCapability();
    });
  }

  private async _probeLocalReconcileCapability(): Promise<void> {
    if (TransportCommand.isExclusiveActive()) {
      scheduleReconcileRecoveryProbe(() => {
        void this._probeLocalReconcileCapability();
      });
      return;
    }

    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    if (!renderer?.id) {
      scheduleReconcileRecoveryProbe(() => {
        void this._probeLocalReconcileCapability();
      });
      return;
    }

    try {
      const C = await this._getConnector();
      await C.getTransportInfoBackground(renderer.id);
      clearReconcileDegraded('local-tier-probe');
      this._restartNowPlayingReconcileMonitor();
      void this._reconcileNowPlayingFromRenderer('probe-recovered');
    } catch (err) {
      const C = await this._getConnector();
      if (C.isLocalTiersExhaustedError(err)) {
        scheduleReconcileRecoveryProbe(() => {
          void this._probeLocalReconcileCapability();
        });
        return;
      }
      scheduleReconcileRecoveryProbe(() => {
        void this._probeLocalReconcileCapability();
      });
    }
  }

  private async _reconcileNowPlayingFromRenderer(source: string) {
    if (this._nowPlayingReconcileInFlight) return;
    if (TransportCommand.isExclusiveActive()) return;
    if (isReconcileDegraded() && source !== 'probe-recovered') return;

    const store = useMaestroStore.getState();
    const zone = store.discovery?.activeZone;
    if (!isNetworkRendererZone(zone)) return;

    const localState = store.playbackState;
    if (localState !== 'playing' && localState !== 'paused' && localState !== 'loading') return;

    const renderer = store.activeConnectorRenderer;
    if (!renderer?.id) return;

    this._nowPlayingReconcileInFlight = true;
    try {
      const C = await this._getConnector();
      const rawResp: string = await C.getTransportInfoBackground(renderer.id);
      const rendererState = parseCurrentTransportState(rawResp);
      const decision = reconcilePlaybackStateFromRenderer({
        localState,
        rendererState,
        playUriRecentMs: TransportCommand.isPlayUriRecent(15000),
        inPlayUriSilenceWindow: TransportCommand.isInPlayUriPostSuccessSilenceWindow(),
        rendererHasStream: store.rendererHasStream,
      });

      if (decision.action === 'set') {
        diagnosticLog('info', 'playback', '[NowPlayingReconcile] sync playbackState from renderer', {
          source,
          rendererId: renderer.id,
          from: localState,
          to: decision.state,
          rendererState,
        });
        store.setPlaybackState(decision.state);
        if (decision.state === 'paused' || decision.state === 'stopped') {
          resetStaleRelTimeTracking();
        }
      }

      if (isReconcileDegraded()) {
        clearReconcileDegraded('now-playing-reconcile');
        this._restartNowPlayingReconcileMonitor();
      }
    } catch (err) {
      const C = await this._getConnector();
      if (C.isLocalTiersExhaustedError(err)) {
        console.debug('[NowPlayingReconcile] local tiers exhausted — halting background poller');
        this._enterReconcileDegraded(source);
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      console.debug('[NowPlayingReconcile] skipped:', message);
    } finally {
      this._nowPlayingReconcileInFlight = false;
    }
  }

  private _applyRendererStoppedState(
    store: ReturnType<typeof useMaestroStore.getState>,
    rendererId: string,
  ): void {
    if (TransportCommand.isPlayUriRecent(15000)) {
      console.log('[TransportState] Suppressing STOPPED — playUri in progress (15s grace)');
      return;
    }

    const { position, duration } = store;
    const nearEnd = duration > 0 && position >= duration * 0.8;
    store.setPlaybackState('stopped');
    resetStaleRelTimeTracking();

    if (nearEnd) {
      void import('./QueueController').then(({ QueueController }) => {
        void QueueController.handleRendererTrackEnded(rendererId, 'transport-stopped');
      });
    }
  }

  private _isTauri(): boolean {
    return detectPlatform().isTauriDesktop;
  }

  private _isIOS(): boolean {
    const result = detectPlatform().isIOSNativeApp;
    if (!this._iosCheckLogged) {
      console.log('[TransportState] _isIOS:', result, '| platform:', detectPlatform().deviceName);
      this._iosCheckLogged = true;
    }
    return result;
  }

  private async _listenTauri() {
    const C = await this._getConnector();

    try {
      this._unlistenTransport = await C.onTransportStateChanged(({ rendererId, state }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        console.log(`[Transport] renderer playback state: ${state} renderer=${rendererId} active=${activeId} storeState=${store.playbackState} ts=${new Date().toISOString()}`);
        if (TransportCommand.isExclusiveActive()) return;
        if (!activeId || rendererId !== activeId) return;

        if (state === 'PLAYING') {
          store.setPlaybackState('playing');
        } else if (state === 'PAUSED_PLAYBACK') {
          if (TransportCommand.isInPlayUriPostSuccessSilenceWindow()) {
            console.log('[Sync] Ignoring stale pause — within 2s of playUri');
          } else if (
            store.rendererHasStream
            && (store.playbackState === 'playing' || store.playbackState === 'loading')
          ) {
            console.log('[TransportState] Ignoring native PAUSED_PLAYBACK — renderer stream + playing');
          } else {
            store.setPlaybackState('paused');
          }
        } else if (state === 'STOPPED' || state === 'NO_MEDIA_PRESENT') {
          this._applyRendererStoppedState(store, rendererId);
        }
      });
    } catch {}

    try {
      const { QueueController } = await import('./QueueController');
      this._unlistenTrackEnded = await C.onRendererTrackEnded(({ rendererId, source }) => {
        if (TransportCommand.isExclusiveActive()) return;
        console.log(
          `[Transport] renderer-track-ended: renderer=${rendererId} source=${source || 'timer'} ts=${new Date().toISOString()}`,
        );
        void QueueController.handleRendererTrackEnded(rendererId, source);
      });
    } catch {}

    try {
      this._unlistenExclusiveFailed = await C.onExclusivePlaybackStartFailed(({ rendererId, reason, suppressWallClockAdvance }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        if (!activeId || rendererId !== activeId) return;
        if (suppressWallClockAdvance !== false) {
          setSuppressWallClockAdvance(true, reason || 'exclusive-playback-start-failed');
        }
        diagnosticLog('warn', 'playback', 'exclusive_playback_start_failed', {
          rendererId,
          reason,
          suppressWallClockAdvance: suppressWallClockAdvance !== false,
        });
      });
    } catch {}

    try {
      this._unlistenExclusiveStarted = await C.onExclusivePlaybackStarted(({ rendererId }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        if (!activeId || rendererId !== activeId) return;
        setSuppressWallClockAdvance(false, 'exclusive-playback-started');
      });
    } catch {}

    try {
      const { handleRendererPositionChanged } = await import('./gaplessTransition');
      this._unlistenPosition = await C.onRendererPositionChanged((payload) => {
        try {
          if (TransportCommand.isExclusiveActive()) return;
          handleRendererPositionChanged(payload);
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          console.error('[position] handler error:', message, err);
          diagnosticLog('error', 'playback', `[position] handler error: ${message}`, {
            rendererId: payload.rendererId,
            relTimeSecs: payload.relTimeSecs,
          });
        }
      });
    } catch {}

    try {
      this._unlistenVolume = await C.onVolumeChanged(({ rendererId, volume }) => {
        const store = useMaestroStore.getState();
        const activeId = store.activeConnectorRenderer?.id;
        if (!activeId || rendererId !== activeId) return;
        if (TransportCommand.isVolumeEchoSuppressed(volume)) return;
        const resolved = resolveInboundDeviceVolume(rendererId, volume);
        store.setVolume(resolved);
      });
    } catch {}
  }

  private _listenIOS() {
    const handleTransport = (event: Event) => {
      const { deviceId: rendererId, state } = (event as CustomEvent).detail || {};
      if (!rendererId || !state) return;
      const store = useMaestroStore.getState();
      const activeId = store.activeConnectorRenderer?.id;
      console.log(`[Transport] renderer playback state: ${state} renderer=${rendererId} active=${activeId} storeState=${store.playbackState} platform=iOS ts=${new Date().toISOString()}`);
      if (TransportCommand.isExclusiveActive()) return;
      if (!activeId || rendererId !== activeId) return;

      if (state === 'PLAYING') {
        store.setPlaybackState('playing');
      } else if (state === 'PAUSED_PLAYBACK') {
        if (TransportCommand.isInPlayUriPostSuccessSilenceWindow()) {
          console.log('[Sync] Ignoring stale pause — within 2s of playUri');
        } else if (
          store.rendererHasStream
          && (store.playbackState === 'playing' || store.playbackState === 'loading')
        ) {
          console.log('[TransportState] Ignoring iOS PAUSED_PLAYBACK — renderer stream + playing');
        } else {
          store.setPlaybackState('paused');
        }
      } else if (state === 'STOPPED' || state === 'NO_MEDIA_PRESENT') {
        this._applyRendererStoppedState(store, rendererId);
      }
    };

    const handleVolume = (event: Event) => {
      const { deviceId, volume, level } = (event as CustomEvent).detail || {};
      const vol = volume ?? level;
      const store = useMaestroStore.getState();
      const activeId = store.activeConnectorRenderer?.id;
      if (activeId && deviceId === activeId) {
        if (TransportCommand.isVolumeEchoSuppressed(vol)) return;
        store.setVolume(resolveInboundDeviceVolume(activeId, vol));
      }
    };

    window.addEventListener('maestroTransportState', handleTransport);
    window.addEventListener('maestroVolumeChanged', handleVolume);
  }

  // ── Playback source ownership (queue / radio / direct) ──

  getTransportOwner(): TransportOwner {
    return this._transportOwner;
  }

  isQueueOwner(): boolean {
    return this._transportOwner === 'queue';
  }

  claimTransportOwner(owner: 'queue' | 'radio' | 'direct'): void {
    if (this._transportOwner === owner) return;
    console.log('[TransportState] owner', this._transportOwner, '→', owner);
    this._transportOwner = owner;
  }

  releaseTransportOwner(): void {
    if (this._transportOwner === null) return;
    console.log('[TransportState] owner', this._transportOwner, '→ null (disarmed)');
    this._transportOwner = null;
  }
}

export const TransportState = new TransportStateImpl();

(window as any).TransportState = TransportState;
